import React, { useEffect, useMemo, useState, useId } from 'react';
import Modal from '../Modal/Modal';
import actionAdminService from '../../services/action-admin.service';
import { agreementsService } from '../../services/agreements.service';
import type { Action } from '../../types/action-admin.types';
import { showToast } from '../../utils/toast';
import { AlertCircle, Phone, PhoneIncoming, PhoneOutgoing, User, FileText, Ticket } from 'lucide-react';
import { useCallQueue } from '../../contexts/CallQueueContext';
import type { QueuedCall } from '../../types/call-queue.types';
import './CallResultModal.css';

interface CallResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  call: QueuedCall;
}

/**
 * Форматирование телефона для отображения
 */
function formatPhone(phone: string): string {
  if (!phone) return '';
  if (phone.includes(' ') || phone.includes('-')) return phone;

  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('7')) {
    return `+7 ${cleaned.slice(1, 4)} ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9)}`;
  }
  return phone;
}

/**
 * Получение названия направления
 */
function getDirectionLabel(direction: QueuedCall['direction']): string {
  switch (direction) {
    case 'inbound':
      return 'Входящий';
    case 'outbound':
      return 'Исходящий';
    case 'dialer':
      return 'Автодозвон';
    default:
      return 'Звонок';
  }
}

/**
 * Получение иконки и названия для subject
 */
function getSubjectInfo(subjectType?: string) {
  switch (subjectType) {
    case 'agreement':
      return { icon: FileText, label: 'Договор', route: '/agreements' };
    case 'lead':
      return { icon: User, label: 'Лид', route: '/leads' };
    case 'ticket':
      return { icon: Ticket, label: 'Тикет', route: '/tickets' };
    default:
      return null;
  }
}

const CallResultModal: React.FC<CallResultModalProps> = ({
  isOpen,
  onClose,
  call,
}) => {
  const { submitResult } = useCallQueue();

  const [selectedActionId, setSelectedActionId] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [actions, setActions] = useState<Action[]>([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const actionFieldId = useId();
  const commentFieldId = useId();
  const actionHintId = `${actionFieldId}-hint`;
  const commentHintId = `${commentFieldId}-hint`;

  const selectedAction = useMemo(() => {
    if (!selectedActionId) return null;
    return actions.find((a) => a.id === Number(selectedActionId)) || null;
  }, [actions, selectedActionId]);

  const subjectInfo = useMemo(() => getSubjectInfo(call.subjectType), [call.subjectType]);
  const DirectionIcon = call.direction === 'inbound' ? PhoneIncoming : call.direction === 'outbound' ? PhoneOutgoing : Phone;

  // Загрузка результатов звонков
  // Для договоров (agreement) сначала загружаем данные договора чтобы получить stage_code,
  // затем используем group_code=agreements и stage_code для загрузки действий
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setActionsLoading(true);
    setError(null);

    (async () => {
      try {
        let stageCode: string | undefined;

        // Для договоров сначала загружаем детали договора чтобы получить stage_code
        if (call.subjectType === 'agreement' && call.subjectId) {
          try {
            const agreementDetail = await agreementsService.getAgreementById(String(call.subjectId));
            stageCode = agreementDetail.stage_code;
          } catch (e) {
            console.warn('[CallResultModal] Failed to load agreement details:', e);
            // Продолжаем без stage_code
          }
        }

        // Формируем параметры для загрузки действий
        const params: {
          group_code: string;
          stage_code?: string;
          include_hidden: boolean;
          include_deleted: boolean;
        } = {
          group_code: call.subjectType === 'agreement' ? 'agreements' : 'call_results',
          include_hidden: false,
          include_deleted: false,
        };

        // Если получили stageCode для договора — добавляем его
        if (call.subjectType === 'agreement' && stageCode) {
          params.stage_code = stageCode;
        }

        if (cancelled) return;

        const list = await actionAdminService.listActions(params);
        if (!cancelled) {
          setActions(list || []);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || 'Не удалось загрузить список результатов');
          setActions([]);
        }
      } finally {
        if (!cancelled) setActionsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, call.subjectType, call.subjectId]);

  const reset = () => {
    setSelectedActionId('');
    setComment('');
    setSaving(false);
    setError(null);
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
    setTimeout(reset, 200);
  };

  const validate = (): string | null => {
    if (!selectedAction) {
      return 'Выберите результат звонка';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const resultCode = selectedAction?.code;
      if (!resultCode) {
        throw new Error('Не удалось определить код результата');
      }

      await submitResult(call.callId, resultCode, comment.trim() || undefined);
      showToast('Результат звонка сохранён', 'success', 1200);
      handleClose();
    } catch (e: any) {
      setError(e?.message || 'Не удалось сохранить результат');
    } finally {
      setSaving(false);
    }
  };

  const isValid = !validate();

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Проставить результат звонка" size="md">
      <form onSubmit={handleSubmit} className="call-result-modal">
        <div className="call-result-modal__hero">
          <div className="call-result-modal__hero-icon" aria-hidden="true">
            <DirectionIcon size={28} />
          </div>
          <div className="call-result-modal__hero-content">
            <p className="call-result-modal__hero-title">{formatPhone(call.phone)}</p>
            <p className="call-result-modal__hero-subtitle">
              {getDirectionLabel(call.direction)}
              {call.customerFullName && ` • ${call.customerFullName}`}
              {call.customerIin && ` • ИИН: ${call.customerIin}`}
            </p>
          </div>
        </div>

        {/* Информация о subject */}
        {subjectInfo && call.subjectId && (
          <div className="call-result-modal__subject">
            <subjectInfo.icon size={16} />
            <a
              href={`${subjectInfo.route}/${call.subjectId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="call-result-modal__subject-link"
            >
              {subjectInfo.label} #{call.subjectId}
            </a>
          </div>
        )}

        <div className="call-result-modal__form-card">
          <div className="call-result-modal__form">
            <div className="form-field">
              <label className="form-label" htmlFor={actionFieldId}>
                Результат звонка <span className="call-result-modal__required">*</span>
              </label>
              {actionsLoading ? (
                <div
                  className="call-result-modal__state call-result-modal__state--loading"
                  role="status"
                >
                  Загрузка результатов…
                </div>
              ) : actions.length === 0 ? (
                <div className="call-result-modal__state call-result-modal__state--empty">
                  Результаты не найдены
                </div>
              ) : (
                <>
                  <select
                    id={actionFieldId}
                    className="input"
                    value={selectedActionId}
                    onChange={(e) => setSelectedActionId(e.target.value)}
                    required
                    aria-invalid={Boolean(error && !selectedAction)}
                    aria-describedby={actions.length > 0 ? actionHintId : undefined}
                  >
                    <option value="">Выберите результат</option>
                    {actions.map((action) => (
                      <option key={action.id} value={action.id}>
                        {action.name}
                      </option>
                    ))}
                  </select>
                  <p id={actionHintId} className="input-hint">
                    Выберите результат звонка из списка
                  </p>
                </>
              )}
            </div>

            <div className="form-field">
              <label className="form-label" htmlFor={commentFieldId}>
                Комментарий
              </label>
              <textarea
                id={commentFieldId}
                className="input call-result-modal__textarea"
                rows={6}
                value={comment}
                placeholder="Добавьте комментарий (необязательно)"
                onChange={(e) => setComment(e.target.value.slice(0, 2000))}
                onKeyDown={(e) => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                    e.preventDefault();
                    void handleSubmit(e as any);
                  }
                }}
                maxLength={2000}
                aria-describedby={commentHintId}
              />
              <p id={commentHintId} className="input-hint">
                Максимум 2000 символов. Быстрая отправка — Ctrl/⌘+Enter
              </p>
            </div>

            {error && (
              <div
                className="alert alert-error call-result-modal__error"
                role="alert"
                aria-live="polite"
              >
                <AlertCircle size={18} />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>

        <div className="modal-actions call-result-modal__actions">
          <button type="button" className="btn-secondary" onClick={handleClose} disabled={saving}>
            Отмена
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={saving || !isValid || actionsLoading}
          >
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CallResultModal;
